import React, { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Users, Plus, X, Check, Send, Gamepad2, Radio, Search, Clock, UserPlus, MessageSquare } from "lucide-react";

const KINDS = [
  { value: "all", label: "All" },
  { value: "invite", label: "Invitations" },
  { value: "request", label: "Requests" },
];

export default function LFG() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState("all");
  const [posts, setPosts] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [gameFilter, setGameFilter] = useState("");
  const [mine, setMine] = useState({ posts: [], applications: [] });
  const [showCreate, setShowCreate] = useState(false);
  const [detail, setDetail] = useState(params.get("post") || null);

  // Sync ?post= param → open modal (used by "View post" CTA on LFG DMs)
  useEffect(() => {
    const pid = params.get("post");
    if (pid && pid !== detail) setDetail(pid);
  }, [params, detail]);
  const closeDetail = () => {
    setDetail(null);
    if (params.get("post")) {
      params.delete("post");
      setParams(params, { replace: true });
    }
  };

  const load = async () => {
    const params = new URLSearchParams();
    if (tab !== "all") params.set("kind", tab);
    if (gameFilter) params.set("game_slug", gameFilter);
    try {
      const r = await api.get(`/lfg?${params.toString()}`);
      setPosts(r.data || []);
    } catch { /* */ }
    if (user) {
      try { const m = await api.get("/lfg/mine"); setMine(m.data); } catch { /* */ }
    }
  };

  useEffect(() => {
    api.get("/games/catalog").then(r => setCatalog(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab, gameFilter, user?.username]);

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="sc-label" style={{ color: "var(--sc-volt)" }}>Looking For Group</div>
          <h1 className="sc-heading text-4xl font-black mt-1">Find your squad</h1>
          <p className="text-sm text-zinc-400 mt-2 max-w-lg">Post an invite to fill your party, or drop a request and let hosts recruit you. Optional: tie your post to a streamer&apos;s community for extra exposure.</p>
        </div>
        {user && (
          <button data-testid="lfg-create-btn" onClick={() => setShowCreate(true)} className="sc-btn flex items-center gap-2">
            <Plus className="w-4 h-4" /> Post an LFG
          </button>
        )}
      </div>

      <div className="flex items-center gap-2 flex-wrap mb-6">
        {KINDS.map(k => (
          <button key={k.value} data-testid={`lfg-tab-${k.value}`} onClick={() => setTab(k.value)} className={`px-3 py-1.5 rounded-sm text-xs sc-mono tracking-widest uppercase ${tab === k.value ? "bg-white/10 text-white" : "text-zinc-500 hover:text-white"}`}>{k.label}</button>
        ))}
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
            <input
              data-testid="lfg-game-filter"
              value={gameFilter}
              onChange={e => setGameFilter(e.target.value)}
              placeholder="Filter by game (e.g. CS:GO, Chess)"
              className="sc-input !pl-8 py-1.5 text-xs w-64"
            />
          </div>
        </div>
      </div>

      {posts.length === 0 && (
        <div className="sc-card p-10 text-center text-zinc-400">
          <Users className="w-8 h-8 mx-auto mb-3 opacity-50" />
          <div>No open posts here yet. {user && "Be the first — start a party."}</div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {posts.map(p => <LfgCard key={p.id} post={p} onOpen={() => setDetail(p.id)} catalog={catalog} />)}
      </div>

      {user && (mine.posts.length > 0 || mine.applications.length > 0) && (
        <div className="mt-10 grid lg:grid-cols-2 gap-4">
          <div className="sc-card p-6">
            <div className="sc-heading text-lg font-bold mb-3">My Posts ({mine.posts.length})</div>
            {mine.posts.length === 0 && <div className="text-xs text-zinc-500">You haven&apos;t posted an LFG yet.</div>}
            {mine.posts.map(p => (
              <div key={p.id} className="flex items-center justify-between text-xs p-3 border border-white/5 rounded-sm mb-1.5 last:mb-0" data-testid={`lfg-mine-post-${p.id}`}>
                <div className="min-w-0">
                  <div className="font-semibold text-zinc-200 truncate">{p.title}</div>
                  <div className="text-zinc-500 sc-mono">{p.kind === "invite" ? "INVITE" : "REQUEST"} · {p.game_slug} · {p.status}</div>
                </div>
                <button onClick={() => setDetail(p.id)} className="sc-btn-ghost text-[10px]">Manage</button>
              </div>
            ))}
          </div>
          <div className="sc-card p-6">
            <div className="sc-heading text-lg font-bold mb-3">My Applications ({mine.applications.length})</div>
            {mine.applications.length === 0 && <div className="text-xs text-zinc-500">You haven&apos;t applied yet.</div>}
            {mine.applications.map(a => (
              <div key={a.id} className="flex items-center justify-between text-xs p-3 border border-white/5 rounded-sm mb-1.5 last:mb-0" data-testid={`lfg-mine-app-${a.id}`}>
                <div className="min-w-0">
                  <div className="text-zinc-200 truncate">{a.message || "(no message)"}</div>
                  <div className="text-zinc-500 sc-mono">Post {a.post_id.slice(0, 8)} · {a.status}</div>
                </div>
                <button onClick={() => setDetail(a.post_id)} className="sc-btn-ghost text-[10px]">View</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {showCreate && <CreateModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} catalog={catalog} />}
      {detail && <DetailModal postId={detail} onClose={closeDetail} onChanged={load} />}
    </div>
  );
}

function LfgCard({ post, onOpen, catalog }) {
  const gameName = catalog.find(g => g.slug === post.game_slug)?.name || post.game_slug;
  const kindColor = post.kind === "invite" ? "var(--sc-volt)" : "var(--sc-pink)";
  const filled = (post.roster?.length || 1);
  return (
    <button onClick={onOpen} data-testid={`lfg-card-${post.id}`} className="sc-card p-5 text-left hover:border-white/20 transition group">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-[10px] sc-mono tracking-widest uppercase px-2 py-0.5 rounded-sm" style={{ background: kindColor, color: "#0A0A0C" }}>{post.kind === "invite" ? "INVITE" : "LFG"}</span>
        <span className="text-[10px] sc-mono tracking-widest uppercase text-zinc-400 flex items-center gap-1"><Gamepad2 className="w-3 h-3" /> {gameName}</span>
        <div className="ml-auto flex items-center gap-1 text-[10px] sc-mono text-zinc-500"><Users className="w-3 h-3" /> {filled}/{post.party_size}</div>
      </div>
      <div className="sc-heading text-lg font-bold group-hover:text-white transition">{post.title}</div>
      {post.description && <p className="text-xs text-zinc-400 mt-1.5 line-clamp-2">{post.description}</p>}
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/5">
        <div className="text-[10px] text-zinc-500">
          By <span className="text-zinc-300 font-semibold">{post.author_username}</span>
          {post.channel_username && <> · in <Link to={`/c/${post.channel_username}`} onClick={e => e.stopPropagation()} className="text-[color:var(--sc-cyan)] hover:underline">@{post.channel_username}</Link></>}
        </div>
        <div className="text-[10px] sc-mono text-zinc-500 flex items-center gap-1"><UserPlus className="w-3 h-3" /> {post.application_count || 0}</div>
      </div>
    </button>
  );
}

function CreateModal({ onClose, onCreated, catalog }) {
  const [form, setForm] = useState({ kind: "invite", game_slug: "", title: "", description: "", party_size: 2, channel_username: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const game = (form.game_slug || "").trim();
      if (!game) { setErr("Please enter a game name."); setBusy(false); return; }
      await api.post("/lfg", { ...form, game_slug: game, channel_username: form.channel_username || null });
      onCreated();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to create");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="sc-card p-6 max-w-lg w-full" onClick={e => e.stopPropagation()} data-testid="lfg-create-modal">
        <div className="flex items-center justify-between mb-4">
          <div className="sc-heading text-xl font-bold">Post an LFG</div>
          <button onClick={onClose}><X className="w-4 h-4 text-zinc-500" /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            {["invite", "request"].map(k => (
              <button
                type="button"
                key={k}
                data-testid={`lfg-kind-${k}`}
                onClick={() => setForm({...form, kind: k})}
                className={`p-3 rounded-sm border text-left ${form.kind === k ? (k === "invite" ? "border-[color:var(--sc-volt)] bg-[color:var(--sc-volt)]/5" : "border-[color:var(--sc-pink)] bg-[color:var(--sc-pink)]/5") : "border-white/10"}`}
              >
                <div className="sc-label" style={{ color: k === "invite" ? "var(--sc-volt)" : "var(--sc-pink)" }}>{k === "invite" ? "Invitation" : "Request"}</div>
                <div className="text-[11px] text-zinc-400 mt-1">{k === "invite" ? "I'm hosting — others can join" : "I want to play — hosts can invite me"}</div>
              </button>
            ))}
          </div>
          <div>
            <label className="sc-label block mb-1">Title</label>
            <input required data-testid="lfg-title" value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="Chill Chess night — casual pace" className="sc-input" maxLength={80} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="sc-label block mb-1">Game</label>
              <input
                required
                data-testid="lfg-game"
                value={form.game_slug}
                onChange={e => setForm({...form, game_slug: e.target.value})}
                placeholder="e.g. Chess, CS:GO, League of Legends"
                className="sc-input"
                maxLength={60}
                list="lfg-game-suggestions"
              />
              <datalist id="lfg-game-suggestions">
                {catalog.filter(g => g.available).map(g => <option key={g.slug} value={g.name} />)}
              </datalist>
            </div>
            <div>
              <label className="sc-label block mb-1">Party Size</label>
              <input data-testid="lfg-size" type="number" min={2} max={12} value={form.party_size} onChange={e => setForm({...form, party_size: Number(e.target.value)})} className="sc-input" />
            </div>
          </div>
          <div>
            <label className="sc-label block mb-1">Description</label>
            <textarea data-testid="lfg-desc" value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Mic optional. Beginners welcome." className="sc-input min-h-[70px]" maxLength={400} />
          </div>
          <div>
            <label className="sc-label block mb-1">Streamer community (optional)</label>
            <input data-testid="lfg-channel" value={form.channel_username} onChange={e => setForm({...form, channel_username: e.target.value.trim()})} placeholder="e.g. demouser — leaves it public if empty" className="sc-input" />
          </div>
          {err && <div className="text-xs text-[color:var(--sc-pink)]">{err}</div>}
          <button data-testid="lfg-submit" disabled={busy} className="sc-btn w-full flex items-center justify-center gap-2">
            <Send className="w-4 h-4" /> {busy ? "Posting…" : "Post LFG"}
          </button>
        </form>
      </div>
    </div>
  );
}

function DetailModal({ postId, onClose, onChanged }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const r = await api.get(`/lfg/${postId}`);
      setData(r.data);
    } catch { onClose(); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [postId]);

  if (!data) return null;
  const { post, applications, my_application, is_owner } = data;

  const apply = async () => {
    setBusy(true); setErr("");
    try {
      await api.post(`/lfg/${postId}/apply`, { message: msg });
      setMsg("");
      await load();
      onChanged?.();
    } catch (e) { setErr(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const act = async (aid, action) => {
    try { await api.post(`/lfg/${postId}/applications/${aid}/${action}`); await load(); onChanged?.(); }
    catch (e) { setErr(e?.response?.data?.detail || "Failed"); }
  };
  const cancel = async () => {
    if (!window.confirm("Cancel this LFG post?")) return;
    try { await api.delete(`/lfg/${postId}`); onChanged?.(); onClose(); }
    catch (e) { setErr(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="sc-card p-6 max-w-lg w-full max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="lfg-detail-modal">
        <div className="flex items-start justify-between mb-4 gap-3">
          <div className="flex-1 min-w-0">
            <span className="text-[10px] sc-mono tracking-widest uppercase px-2 py-0.5 rounded-sm inline-block mb-2" style={{ background: post.kind === "invite" ? "var(--sc-volt)" : "var(--sc-pink)", color: "#0A0A0C" }}>{post.kind === "invite" ? "INVITE" : "REQUEST"}</span>
            <div className="sc-heading text-xl font-bold">{post.title}</div>
            <div className="text-xs text-zinc-500 mt-1 sc-mono">
              {post.game_slug} · {post.roster?.length || 1}/{post.party_size} · by {post.author_username}
              {post.channel_username && <> · in <Link to={`/c/${post.channel_username}`} className="text-[color:var(--sc-cyan)] hover:underline">@{post.channel_username}</Link></>}
            </div>
          </div>
          <button onClick={onClose}><X className="w-4 h-4 text-zinc-500" /></button>
        </div>

        {post.description && <p className="text-sm text-zinc-300 mb-4">{post.description}</p>}

        <div className="border-t border-white/5 pt-3 mb-3">
          <div className="sc-label mb-2 flex items-center gap-1"><Clock className="w-3 h-3" /> Roster</div>
          <div className="flex flex-wrap gap-1">
            {(post.roster || []).map(u => <span key={u} className="text-[10px] sc-mono px-2 py-1 rounded-sm border border-white/10">{u}</span>)}
          </div>
        </div>

        {!is_owner && user && (post.status === "open" || (post.status === "full" && my_application)) && (
          <div className="border-t border-white/5 pt-3">
            {my_application ? (
              <div
                className="text-xs sc-mono p-3 border rounded-sm"
                style={{ borderColor: my_application.status === "accepted" ? "var(--sc-volt)" : my_application.status === "declined" ? "var(--sc-pink)" : "rgba(255,255,255,0.1)" }}
                data-testid="lfg-my-app-status"
              >
                <div className="flex items-center justify-between gap-2">
                  <div>
                    Your application is <span className="font-bold">{my_application.status.toUpperCase()}</span>
                  </div>
                  <button
                    data-testid="lfg-message-owner"
                    onClick={() => nav(`/messages?to=${encodeURIComponent(post.author_username)}`)}
                    className="sc-btn-ghost text-[10px] flex items-center gap-1"
                    title={`DM ${post.author_username}`}
                  >
                    <MessageSquare className="w-3 h-3" /> Message @{post.author_username}
                  </button>
                </div>
                {my_application.status === "accepted" && (
                  <div className="mt-2 text-[10px] text-zinc-400 normal-case">You&apos;re in the roster — head to your DMs to coordinate with the host.</div>
                )}
                {my_application.status === "pending" && (
                  <div className="mt-2 text-[10px] text-zinc-500 normal-case">We&apos;ve also sent an &ldquo;LFG Application&rdquo; DM to @{post.author_username} so they see your ping instantly.</div>
                )}
              </div>
            ) : (
              <>
                <label className="sc-label block mb-1">{post.kind === "invite" ? "Message the host" : "Message the player"}</label>
                <textarea data-testid="lfg-apply-msg" value={msg} onChange={e => setMsg(e.target.value)} placeholder="Say hi and share your experience" className="sc-input min-h-[60px] text-xs" maxLength={200} />
                <button data-testid="lfg-apply-submit" disabled={busy} onClick={apply} className="sc-btn w-full mt-2 flex items-center justify-center gap-2">
                  <Send className="w-4 h-4" /> {busy ? "Sending…" : (post.kind === "invite" ? "Request to join" : "Offer to host")}
                </button>
                <div className="text-[10px] text-zinc-500 mt-2 leading-relaxed">
                  When you send, we auto-DM @{post.author_username} with an <span className="text-[color:var(--sc-cyan)] font-semibold">LFG Application</span> tag so they can respond immediately.
                </div>
              </>
            )}
          </div>
        )}

        {is_owner && (
          <div className="border-t border-white/5 pt-3">
            <div className="sc-label mb-2">Applications ({applications.length})</div>
            {applications.length === 0 && <div className="text-xs text-zinc-500">No applications yet.</div>}
            {applications.map(a => (
              <div key={a.id} className="flex items-start justify-between gap-2 p-3 border border-white/5 rounded-sm mb-1.5" data-testid={`lfg-app-${a.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-semibold">{a.applicant_username}</div>
                  <div className="text-[11px] text-zinc-400 mt-0.5">{a.message || "(no message)"}</div>
                  <div className="text-[9px] sc-mono text-zinc-500 mt-1 flex items-center gap-2">
                    <span>{a.status}</span>
                    <button
                      data-testid={`lfg-app-message-${a.id}`}
                      onClick={() => nav(`/messages?to=${encodeURIComponent(a.applicant_username)}`)}
                      className="underline hover:text-[color:var(--sc-cyan)] flex items-center gap-1 normal-case"
                    >
                      <MessageSquare className="w-3 h-3" /> Message
                    </button>
                  </div>
                </div>
                {a.status === "pending" && (
                  <div className="flex flex-col gap-1">
                    <button onClick={() => act(a.id, "accept")} data-testid={`lfg-app-accept-${a.id}`} className="p-1.5 rounded-sm bg-[color:var(--sc-volt)] text-black hover:brightness-110" title="Accept — auto-DMs an LFG Response to the applicant"><Check className="w-3.5 h-3.5" /></button>
                    <button onClick={() => act(a.id, "decline")} data-testid={`lfg-app-decline-${a.id}`} className="p-1.5 rounded-sm border border-[color:var(--sc-pink)] text-[color:var(--sc-pink)] hover:bg-[color:var(--sc-pink)]/10" title="Decline"><X className="w-3.5 h-3.5" /></button>
                  </div>
                )}
              </div>
            ))}
            <button onClick={cancel} data-testid="lfg-cancel" className="text-xs text-[color:var(--sc-pink)] hover:underline mt-3">Cancel this post</button>
          </div>
        )}

        {err && <div className="text-xs text-[color:var(--sc-pink)] mt-3">{err}</div>}
      </div>
    </div>
  );
}
